import { ArrowDropDown } from "@mui/icons-material";
import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  MenuItemProps as MuiMenuItemProps,
  Paper,
  Popper,
  PopperProps,
  SxProps,
  Theme,
  Tooltip,
} from "@mui/material";
import { PopperPlacementType } from "@mui/material/Popper/BasePopper.types";
import { ReactNode, useRef, useState } from "react";

export type MenuItemProps = MuiMenuItemProps & {
  label: string;
  dataTest?: string;
};

type DropdownMenuProps = {
  buttonLabel: string;
  tooltipText: string;
  menuItems: MenuItemProps[];
  buttonIcon?: ReactNode;
  buttonColor?: "inherit" | "success" | "info" | "warning" | "error" | "primary" | "secondary";
  buttonVariant?: "contained" | "outlined" | "text";
  placement?: PopperPlacementType;
  size?: "small" | "medium" | "large";
  PopperProps?: PopperProps;
  menuDataTest?: string;
  sx?: SxProps<Theme>;
};

/**
 * Custom dropdown menu component for more control over the button and menu items rather than using the built-in MUI Menu component.
 */
const DropdownMenu = ({
  buttonLabel,
  tooltipText,
  menuItems,
  buttonIcon,
  buttonColor = "primary",
  buttonVariant = "outlined",
  placement = "bottom-start",
  size = "small",
  PopperProps,
  menuDataTest,
  sx,
}: DropdownMenuProps) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  return (
    <>
      <Tooltip title={tooltipText} arrow>
        <ButtonGroup
          size={size}
          color={buttonColor}
          variant={buttonVariant}
          ref={anchorRef}
          aria-label={`${buttonLabel} options`}
          data-test={menuDataTest}
          sx={sx}
        >
          <Button size={size} onClick={handleToggle} startIcon={buttonIcon} fullWidth>
            {buttonLabel}
          </Button>
          <Button
            size={size}
            aria-expanded={open ? "true" : undefined}
            aria-haspopup="menu"
            onClick={handleToggle}
          >
            <ArrowDropDown />
          </Button>
        </ButtonGroup>
      </Tooltip>
      <Popper
        sx={{
          zIndex: 999,
          minWidth: anchorRef.current?.clientWidth,
          ...PopperProps?.sx,
        }}
        open={open}
        anchorEl={anchorRef.current}
        placement={placement}
        role={undefined}
        transition
        disablePortal
        {...PopperProps}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem>
                  {menuItems.map((item, index) => {
                    const { label, dataTest, ...rest } = item;
                    return (
                      <MenuItem
                        key={index}
                        {...rest}
                        data-test={item.dataTest}
                        onClick={
                          !item.onClick
                            ? undefined
                            : (ev) => {
                                item.onClick!(ev);
                                setOpen(false);
                              }
                        }
                      >
                        {item.label}
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default DropdownMenu;
